import { FC, useEffect, useMemo, useState, type HTMLAttributes } from 'react'
import './group-promo-checklist.css'

import * as Icons from '@icons'
import Card from '@components/atoms/card'
import CollapsibleElement from '@components/atoms/collapsable-element'

import { priceFormatter } from '@utils/number-helper'
import { getStorageItem, setStorageItem } from '@utils/storage-helper'
import { flatten } from '@utils/string-helper'


interface Props extends HTMLAttributes<HTMLDivElement> {
    groupId: string
    promoChecklist: GroupPromoChecklist
    groupStatus: 'active' | 'completed' | 'canceled' | 'finalized' | 'pending'
    onInvitePeople?: () => void
}

const getPromoChecklistStorageKey = (groupId: string) => `group-${groupId}-promo-checklist-open-v2`
const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
})

const formatPromoAmount = (amount: number) => {
    const value = amount / 100
    if (Number.isInteger(value)) return compactCurrencyFormatter.format(value)
    return priceFormatter.format(value)
}

const GroupPromoChecklist: FC<Props> = (props) => {
    const { className: classProps, groupId, promoChecklist, groupStatus, onInvitePeople, ...divProps } = props
    const className = flatten('group-promo-checklist', classProps)
    const storageKey = useMemo(() => getPromoChecklistStorageKey(groupId), [groupId])
    const [isOpen, setIsOpen] = useState(() => {
        const storedValue = getStorageItem('localStorage', storageKey)
        if (storedValue === 'true') return true
        if (storedValue === 'false') return false
        return false
    })

    useEffect(() => {
        setStorageItem('localStorage', storageKey, `${isOpen}`)
    }, [storageKey, isOpen])

    const rewardLabel = formatPromoAmount(promoChecklist.rewardAmount)
    const participantProgress = Math.max(0, Math.min(
        promoChecklist.participantProgress,
        promoChecklist.participantThreshold
    ))
    const spendProgressAmount = Math.max(0, Math.min(
        promoChecklist.spendProgressAmount,
        promoChecklist.spendThresholdAmount
    ))
    const participantComplete = participantProgress >= promoChecklist.participantThreshold
    const spendComplete = spendProgressAmount >= promoChecklist.spendThresholdAmount
    const completedCount = Number(participantComplete) + Number(spendComplete)
    const showInvitePeople = !participantComplete && groupStatus === 'active' && !!onInvitePeople
    const participantPercent = Math.round((participantProgress / Math.max(promoChecklist.participantThreshold, 1)) * 100)
    const spendPercent = Math.round((spendProgressAmount / Math.max(promoChecklist.spendThresholdAmount, 1)) * 100)
    const compactProgressSummary = promoChecklist.state === 'applied'
        ? ''
        : promoChecklist.state === 'earned'
            ? `${rewardLabel} earned`
            : `${participantProgress}/${promoChecklist.participantThreshold} people • ${formatPromoAmount(spendProgressAmount)}/${formatPromoAmount(promoChecklist.spendThresholdAmount)} spent`

    const title = promoChecklist.state === 'applied'
        ? `${rewardLabel} applied to this group`
        : promoChecklist.state === 'earned'
            ? `You earned ${rewardLabel} for this group`
            : `Earn ${rewardLabel} for this group`

    const summary = promoChecklist.state === 'applied'
        ? 'Applied'
        : promoChecklist.state === 'earned'
            ? 'Earned'
            : `${completedCount} of 2 complete`

    const footnote = promoChecklist.state === 'applied'
        ? `${rewardLabel} has been applied to this group's ledger.`
        : promoChecklist.state === 'earned'
            ? `Your ${rewardLabel} credit is on the way and will be applied to this group's ledger.`
            : "Applied to this group's ledger once both goals are met."

    return (
        <Card light className={className} {...divProps}>
            <CollapsibleElement
                state={[isOpen, setIsOpen]}
                initState={promoChecklist.state === 'in_progress' ? 'open' : 'closed'}
            >
                <div className='group-promo-checklist-trigger'>
                    <div className='group-promo-checklist-trigger-copy'>
                        <div className='group-promo-checklist-chevron'>
                            <div className='group-promo-checklist-chevron-badge'>
                                <Icons.Chevron
                                    facing={isOpen ? 'down' : 'up'}
                                    width='1.1rem'
                                    height='1.1rem'
                                    tint='var(--splitcard-gray-500)'
                                />
                            </div>
                        </div>
                        <div className='group-promo-checklist-topline'>
                            <p className='group-promo-checklist-eyebrow'>First group bonus</p>
                            <p className={flatten(
                                'group-promo-checklist-status',
                                promoChecklist.state === 'applied' ? 'is-applied' : ''
                            )}>{summary}</p>
                        </div>
                        <div className='group-promo-checklist-heading-copy'>
                            <div className='group-promo-checklist-title-row'>
                                <div className='group-promo-checklist-icon-badge'>
                                    <Icons.GiftBox
                                        width='1.25rem'
                                        height='1.25rem'
                                        tint='var(--splitcard-blue-700)'
                                    />
                                </div>
                                <div className='group-promo-checklist-copy-block'>
                                    <h3 className='group-promo-checklist-title'>{title}</h3>
                                    {!!compactProgressSummary && <p className='group-promo-checklist-summary'>{compactProgressSummary}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='group-promo-checklist-content'>
                    {promoChecklist.state === 'earned' && (
                        <div className='group-promo-checklist-earned-banner'>
                            <Icons.Check
                                type='success'
                                width='1.25rem'
                                height='1.25rem'
                                tint='var(--splitcard-green-600)'
                            />
                            <p className='text-body-2'>
                                <strong>{`${rewardLabel} is on the way.`}</strong>
                            </p>
                        </div>
                    )}
                    <PromoChecklistRow
                        complete={participantComplete}
                        helperText='Organizer included. Add more people to qualify.'
                        iconPath='/icons/duotone/group-of-three.svg'
                        label={`Reach ${promoChecklist.participantThreshold} total people in this group`}
                        progressPercent={participantPercent}
                        progressLabel={`${participantProgress} / ${promoChecklist.participantThreshold}`}
                    />
                    <PromoChecklistRow
                        complete={spendComplete}
                        helperText='Tracked automatically from group card spend.'
                        iconPath='/icons/duotone/dollar-sign.svg'
                        label={`Spend ${formatPromoAmount(promoChecklist.spendThresholdAmount)} on this group card`}
                        progressPercent={spendPercent}
                        progressLabel={`${formatPromoAmount(spendProgressAmount)} / ${formatPromoAmount(promoChecklist.spendThresholdAmount)}`}
                    />
                    {showInvitePeople && (
                        <div className='group-promo-checklist-action-row'>
                            <button
                                type='button'
                                className='group-promo-checklist-action'
                                onClick={onInvitePeople}
                            >
                                Invite people
                                <Icons.Arrow
                                    facing='right'
                                    width='0.9rem'
                                    height='0.9rem'
                                    tint='var(--splitcard-blue-600)'
                                />
                            </button>
                        </div>
                    )}
                    <p className='group-promo-checklist-footnote text-body-2'>{footnote}</p>
                </div>
            </CollapsibleElement>
        </Card>
    )
}

interface PromoChecklistRowProps {
    complete: boolean
    helperText: string
    iconPath: string
    label: string
    progressPercent: number
    progressLabel: string
}

const PromoChecklistRow: FC<PromoChecklistRowProps> = ({
    complete,
    helperText,
    iconPath,
    label,
    progressPercent,
    progressLabel,
}) => {
    return (
        <div className={flatten('group-promo-checklist-row', { 'is-complete': complete })}>
            <div className='group-promo-checklist-row-icon' style={{ backgroundImage: `url(${iconPath})` }} />
            <div className='group-promo-checklist-row-copy'>
                <p className='group-promo-checklist-row-label'>{label}</p>
                <p className='group-promo-checklist-row-helper text-body-2'>{helperText}</p>
                <div className='group-promo-checklist-row-progressbar'>
                    <div
                        className='group-promo-checklist-row-progressfill'
                        style={{ width: `${Math.max(0, Math.min(progressPercent, 100))}%` }}
                    />
                </div>
            </div>
            <div className='group-promo-checklist-row-meta'>
                <div className='group-promo-checklist-row-progresspill'>{progressLabel}</div>
                {complete && (
                    <Icons.CheckSmall
                        type='bordered'
                        width='1rem'
                        height='1rem'
                        tint='var(--splitcard-green-600)'
                    />
                )}
            </div>
        </div>
    )
}

export default GroupPromoChecklist
